/**
 * AddToHomeScreen — Lightweight PWA install prompt (bottom-sheet toast).
 *
 * Shows a non-intrusive bottom toast on mobile ONLY for returning, engaged users.
 * Uses the `beforeinstallprompt` event to trigger the native install flow.
 *
 * Visibility requires ALL of:
 *   1. Browser fires `beforeinstallprompt` (Chrome/Edge/Samsung)
 *   2. Not already installed (standalone mode)
 *   3. localStorage 'fp_returning_user' is truthy
 *   4. localStorage 'fp_has_engaged' is truthy
 *   5. Not dismissed within the last 7 days
 *
 * Setting the keys (done externally via exported helpers):
 *   - fp_returning_user: set after 30s on site OR a successful property lookup
 *   - fp_has_engaged: set after a successful property lookup OR a completed purchase
 *
 * iOS Safari doesn't fire `beforeinstallprompt` — this component simply won't render there.
 */

import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import { Download, X } from "lucide-react";
import {
  COOKIE_CONSENT_EVENT,
  getCookieConsent,
} from "./CookieConsentBanner";

// Keys for persistence
const DISMISS_KEY = "fp_pwa_dismissed_at";
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const RETURNING_KEY = "fp_returning_user";
const ENGAGED_KEY = "fp_has_engaged";
const FILED_KEY = "fp_protest_filed";

// Extend Window to include the beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// ---------------------------------------------------------------------------
// Exported helpers — call these from hooks/services at the right moments
// ---------------------------------------------------------------------------

/** Mark the user as returning. Call after 30s on site or successful lookup. */
export function markReturningUser(): void {
  localStorage.setItem(RETURNING_KEY, "1");
}

/** Mark the user as engaged. Call after successful lookup or purchase. */
export function markUserEngaged(): void {
  localStorage.setItem(ENGAGED_KEY, "1");
}

/** Mark the user as having filed their protest. Call after "Mark as Filed" confirmation. */
export function markProtestFiled(): void {
  localStorage.setItem(FILED_KEY, "1");
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Routes where the PWA banner should never appear (conversion-critical flows). */
const SUPPRESSED_ROUTES = [
  "/a/",
  "/check",
  "/dashboard",
  "/wizard",
  "/checkout",
  "/payment-success",
  "/guest/packet",
  "/property-tax-appeal/",
  "/packet/sample",
  "/pricing",
  "/vs-appealdesk",
  "/dev",
];

export function AddToHomeScreen() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  // Suppress on conversion-critical routes
  const isSuppressed =
    location.pathname === "/" ||
    SUPPRESSED_ROUTES.some((r) => location.pathname.startsWith(r));

  // Check eligibility whenever the deferred prompt becomes available
  useEffect(() => {
    if (isStandalone() || isDismissed() || !isEligible()) {
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);

      // Show immediately if already eligible
      if (isEligible()) {
        setVisible(true);
      }
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Re-check eligibility when localStorage might have changed (e.g. after lookup)
  useEffect(() => {
    if (!deferredPrompt || visible) return;
    if (isEligible() && !isDismissed()) {
      setVisible(true);
    }
  }, [deferredPrompt, visible]);

  // Never compete with the consent decision for the same viewport space.
  // Consent changes in this tab do not emit the browser `storage` event, so
  // CookieConsentBanner publishes a small local event after either choice.
  useEffect(() => {
    const handleConsentChange = () => {
      if (deferredPrompt && isEligible() && !isDismissed()) {
        setVisible(true);
      }
    };

    window.addEventListener(COOKIE_CONSENT_EVENT, handleConsentChange);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, handleConsentChange);
  }, [deferredPrompt]);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === "accepted") {
        setVisible(false);
        setDeferredPrompt(null);
      }
    } catch {
      setVisible(false);
    }
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    setDeferredPrompt(null);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  }, []);

  if (isSuppressed) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-3 left-3 right-3 z-50 transition-transform duration-300 ease-out md:left-auto md:max-w-md ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
      role="banner"
      aria-label="Install FairPath app"
      aria-hidden={!visible}
    >
      <div className="bg-fp-navy-900 text-white px-3 py-3 rounded-fp shadow-fp-card flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Download className="h-4 w-4 shrink-0 text-fp-emerald-500" />
          <span className="truncate">
            Add FairPath to your home screen for quick access
          </span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleInstall}
            className="bg-fp-emerald-500 hover:bg-fp-emerald-600 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
          >
            Install
          </button>
          <button
            onClick={handleDismiss}
            className="text-slate-400 hover:text-white p-1 transition-colors"
            aria-label="Dismiss install prompt"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isStandalone(): boolean {
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  if ((navigator as any).standalone === true) return true;
  return false;
}

function isDismissed(): boolean {
  const timestamp = localStorage.getItem(DISMISS_KEY);
  if (!timestamp) return false;

  const dismissedAt = parseInt(timestamp, 10);
  if (isNaN(dismissedAt)) return false;

  return Date.now() - dismissedAt < DISMISS_WINDOW_MS;
}

/** All three keys must be truthy — prompt only fires after user confirms filing. */
function isEligible(): boolean {
  return (
    getCookieConsent() !== null &&
    !!localStorage.getItem(RETURNING_KEY) &&
    !!localStorage.getItem(ENGAGED_KEY) &&
    !!localStorage.getItem(FILED_KEY)
  );
}
